import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from './LanguageProvider';
import { useI18n } from '../i18n/i18n';
import { useCurrentLanguage } from './LanguageRedirect';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { Button } from './ui/button';
import { X } from 'lucide-react';

// Define cookie categories and their default states
interface CookieCategory {
  id: string;
  required: boolean;
  checked: boolean;
}

// Initial cookie categories
const initialCategories: CookieCategory[] = [
  { id: 'essential', required: true, checked: true },
  { id: 'preferences', required: false, checked: false },
  { id: 'analytics', required: false, checked: false },
  { id: 'marketing', required: false, checked: false },
];

// Set cookie with expiration
const setCookie = (name: string, value: string, days: number) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

// Get cookie by name
const getCookie = (name: string) => {
  const nameEQ = `${name}=`;
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

const CookieConsent: React.FC = () => {
  const { language } = useLanguage();
  const currentLang = useCurrentLanguage();
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [categories, setCategories] = useState<CookieCategory[]>(initialCategories);
  const [dismissed, setDismissed] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<Element | null>(null);

  // Auto-dismiss timer
  useEffect(() => {
    let dismissTimer: NodeJS.Timeout;

    if (visible && !showPreferences && !dismissed) {
      dismissTimer = setTimeout(() => {
        setVisible(false);
        setDismissed(true);
      }, 60000); // 60 seconds
    }

    return () => {
      if (dismissTimer) clearTimeout(dismissTimer);
    };
  }, [visible, showPreferences, dismissed]);

  // Check for consent cookie on load
  useEffect(() => {
    const hasConsent = getCookie('cookie_consent');
    if (!hasConsent) {
      // Show banner after 500ms delay on first visit
      const timer = setTimeout(() => {
        setVisible(true);
      }, 500);

      return () => clearTimeout(timer);
    } else {
      // Load saved preferences and apply them to Google Consent Mode
      try {
        const savedPreferences = localStorage.getItem('cookie_preferences');
        if (savedPreferences) {
          const prefs = JSON.parse(savedPreferences);
          setCategories(prefs);
          
          // Apply saved preferences to Google Consent Mode
          if (typeof window !== 'undefined' && (window as any).gtag) {
            const gtag = (window as any).gtag;
            const analytics = prefs.find((cat: CookieCategory) => cat.id === 'analytics')?.checked || false;
            const marketing = prefs.find((cat: CookieCategory) => cat.id === 'marketing')?.checked || false;
            
            gtag('consent', 'update', {
              'analytics_storage': analytics ? 'granted' : 'denied',
              'ad_storage': marketing ? 'granted' : 'denied',
              'ad_user_data': marketing ? 'granted' : 'denied',
              'ad_personalization': marketing ? 'granted' : 'denied'
            });
          }
        }
      } catch (error) {
        console.error('Error loading cookie preferences:', error);
      }
    }
  }, []);

  // Focus management for accessibility
  useEffect(() => {
    if (visible && bannerRef.current) {
      // Store the previously focused element
      previousActiveElement.current = document.activeElement;

      // Find the first focusable element and focus it
      const focusableElements = bannerRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length > 0) {
        (focusableElements[0] as HTMLElement).focus();
      }
    } else if (!visible && previousActiveElement.current) {
      // Restore focus when banner is closed
      (previousActiveElement.current as HTMLElement).focus();
    }
  }, [visible]);

  // Handle trap focus in modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!showPreferences || !bannerRef.current) return;

      // Only handle Tab key
      if (e.key !== 'Tab') return;

      const focusableElements = bannerRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      // Shift + Tab on first element goes to last element
      if (e.shiftKey && document.activeElement === firstElement) {
        lastElement.focus();
        e.preventDefault();
      } 
      // Tab on last element goes to first element
      else if (!e.shiftKey && document.activeElement === lastElement) {
        firstElement.focus();
        e.preventDefault();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showPreferences]);

  const handleAcceptAll = () => {
    const updatedCategories = categories.map(cat => ({
      ...cat,
      checked: true
    }));
    savePreferences(updatedCategories);
  };

  const handleRejectAll = () => {
    const updatedCategories = categories.map(cat => ({
      ...cat,
      checked: cat.required // Only keep required cookies
    }));
    savePreferences(updatedCategories);
  };

  const toggleCategory = (categoryId: string) => {
    setCategories(prevCategories => 
      prevCategories.map(cat => 
        cat.id === categoryId && !cat.required ? { ...cat, checked: !cat.checked } : cat
      )
    );
  };

  const savePreferences = (prefsToSave = categories) => {
    // Save to localStorage
    localStorage.setItem('cookie_preferences', JSON.stringify(prefsToSave));

    // Set a consent cookie that expires in 12 months
    setCookie('cookie_consent', 'true', 365);

    // Apply the preferences (in a real implementation, this would enable/disable specific cookies)
    const acceptedCategories = prefsToSave
      .filter(cat => cat.checked)
      .map(cat => cat.id)
      .join(',');
    setCookie('cookie_categories', acceptedCategories, 365);

    // Update Google Consent Mode based on user preferences
    if (typeof window !== 'undefined' && (window as any).gtag) {
      const gtag = (window as any).gtag;
      
      // Find which categories were accepted
      const analytics = prefsToSave.find(cat => cat.id === 'analytics')?.checked || false;
      const marketing = prefsToSave.find(cat => cat.id === 'marketing')?.checked || false;
      
      // Update consent for Google Analytics
      gtag('consent', 'update', {
        'analytics_storage': analytics ? 'granted' : 'denied',
        'ad_storage': marketing ? 'granted' : 'denied',
        'ad_user_data': marketing ? 'granted' : 'denied',
        'ad_personalization': marketing ? 'granted' : 'denied'
      });
      
      console.log('Google Consent updated:', { analytics, marketing });
    }

    // Close the banner and modal
    setShowPreferences(false);
    setVisible(false);
    setDismissed(true);
  };

  const handleCancel = () => {
    setShowPreferences(false);
  };

  // If neither the banner nor preferences modal is visible, don't render anything
  if (!visible && !showPreferences) return null;

  return (
    <>
      {/* Main Cookie Banner */}
      {visible && !showPreferences && (
        <div 
          ref={bannerRef}
          aria-live="polite"
          className="fixed bottom-0 left-0 right-0 z-50 bg-white shadow-lg transform transition-transform duration-300 ease-in-out animate-slide-up"
          style={{ 
            boxShadow: '0 -4px 10px rgba(0, 0, 0, 0.1)',
          }}
        >
          <div className="max-w-7xl mx-auto p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm text-gray-700">
                  {t('cookieConsent.message')} 
                  <a href={`/${currentLang}/cookies`} className="text-primary hover:underline ml-1">
                    {t('cookieConsent.learnMore')}
                  </a>
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <Button 
                  variant="default" 
                  onClick={handleAcceptAll}
                  className="w-full sm:w-auto text-sm"
                  size="sm"
                >
                  {t('cookieConsent.acceptAll')}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleRejectAll}
                  className="w-full sm:w-auto text-sm"
                  size="sm"
                >
                  {t('cookieConsent.rejectAll')}
                </Button>
                <Button 
                  variant="secondary" 
                  onClick={() => setShowPreferences(true)}
                  className="w-full sm:w-auto text-sm"
                  size="sm"
                >
                  {t('cookieConsent.manage')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      <Dialog open={showPreferences} onOpenChange={(open) => {
        if (!open) handleCancel();
      }}>
        <DialogContent className="sm:max-w-lg w-[95vw] max-w-[95vw] p-4 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('cookieConsent.preferences.title')}</DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-6">
              {t('cookieConsent.preferences.description')}
            </p>

            <div className="space-y-4">
              {categories.map((category) => (
                <div key={category.id} className="flex items-start space-x-3 border-b border-gray-100 pb-4">
                  <Checkbox
                    id={`cookie-${category.id}`}
                    checked={category.checked}
                    onCheckedChange={() => toggleCategory(category.id)}
                    disabled={category.required}
                    className="mt-1"
                  />
                  <div>
                    <label
                      htmlFor={`cookie-${category.id}`}
                      className="text-sm font-medium leading-none cursor-pointer"
                    >
                      {t(`cookieConsent.categories.${category.id}.title`)}
                      {category.required && (
                        <span className="text-xs ml-2 text-muted-foreground">
                          ({t('cookieConsent.required')})
                        </span>
                      )}
                    </label>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-1 break-words">
                      {t(`cookieConsent.categories.${category.id}.description`)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>
              {t('cookieConsent.preferences.cancel')}
            </Button>
            <Button onClick={() => savePreferences()}>
              {t('cookieConsent.preferences.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;